import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { FileText, Youtube, Mic, Loader2, Lock, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SourceDropzone } from "@/components/SourceDropzone";
import { fetchYoutubeTranscript, transcribeAudio } from "@/lib/transcription.functions";

export type Mode = "text" | "youtube" | "audio";

const AUDIO_MAX_BYTES = 20 * 1024 * 1024; // 20 MB

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Couldn't read file"));
    reader.readAsDataURL(file);
  });
}

export function SourceInputTabs({
  value,
  onChange,
  disabled = false,
  lockedMessage,
  onModeChange,
  appendOnUpload = false,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  lockedMessage?: string;
  onModeChange?: (mode: Mode) => void;
  /** When true, YouTube + audio loads append to value with a labeled header instead of replacing. */
  appendOnUpload?: boolean;
}) {
  const [mode, setMode] = useState<Mode>("text");
  const [ytUrl, setYtUrl] = useState("");
  const [ytLoading, setYtLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioFileName, setAudioFileName] = useState<string | null>(null);
  const [sourceCount, setSourceCount] = useState(0);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const fetchYt = useServerFn(fetchYoutubeTranscript);
  const transcribe = useServerFn(transcribeAudio);

  function appendOrReplace(text: string, label: string) {
    if (appendOnUpload && value.trim()) {
      const nextIdx = (sourceCount || 1) + 1;
      const header = `\n\n=== Source ${nextIdx}: ${label} ===\n\n`;
      onChange(value.trimEnd() + header + text);
      setSourceCount(nextIdx);
    } else {
      onChange(appendOnUpload ? `=== Source 1: ${label} ===\n\n${text}` : text);
      setSourceCount(1);
    }
  }

  async function loadYoutube() {
    if (disabled) return;
    if (!ytUrl.trim()) return toast.error("Paste a YouTube link first.");
    setYtLoading(true);
    try {
      const res = await fetchYt({ data: { url: ytUrl.trim() } });
      appendOrReplace(res.text, `YouTube — ${ytUrl.trim()}`);
      toast.success(`Loaded transcript (${res.text.split(/\s+/).length.toLocaleString()} words).`);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't fetch that transcript.");
    } finally {
      setYtLoading(false);
    }
  }

  async function handleAudioFile(file: File) {
    if (disabled) return;
    if (file.size > AUDIO_MAX_BYTES) {
      return toast.error("Audio file is too large (max 20 MB). Try compressing or trimming it.");
    }
    setAudioLoading(true);
    setAudioFileName(file.name);
    try {
      const base64 = await fileToBase64(file);
      const mimeType = file.type || guessMime(file.name);
      const res = await transcribe({ data: { data: base64, mimeType, filename: file.name } });
      appendOrReplace(res.text, `Audio — ${file.name}`);
      toast.success(`Transcribed (${res.text.split(/\s+/).length.toLocaleString()} words).`);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't transcribe that file.");
      setAudioFileName(null);
    } finally {
      setAudioLoading(false);
    }
  }

  const tabs: { id: Mode; label: string; icon: typeof FileText }[] = [
    { id: "text", label: "Text / file", icon: FileText },
    { id: "youtube", label: "YouTube", icon: Youtube },
    { id: "audio", label: "Audio / video", icon: Mic },
  ];

  return (
    <div className="mt-3 space-y-3">
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = mode === t.id;
          return (
          <button
              key={t.id}
              type="button"
              onClick={() => {
                setMode(t.id);
                onModeChange?.(t.id);
              }}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition sm:text-sm ${
                active ? "bg-card shadow-soft" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {mode === "text" && (
        <SourceDropzone value={value} onChange={onChange} disabled={disabled} lockedMessage={lockedMessage} appendOnUpload={appendOnUpload} />
      )}

      {mode === "youtube" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={ytUrl}
              onChange={(e) => setYtUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=…"
              disabled={disabled || ytLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void loadYoutube();
                }
              }}
            />
            <Button type="button" onClick={loadYoutube} disabled={disabled || ytLoading || !ytUrl.trim()}>
              {ytLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Works with any public YouTube video that has captions (auto-generated or manual). Lectures, talks, podcast videos, Khan Academy, etc.
          </p>
          {value && !ytLoading && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Transcript loaded</span> — {value.split(/\s+/).length.toLocaleString()} words. You can edit it below before generating.
            </div>
          )}
        </div>
      )}

      {mode === "audio" && (
        <div className="space-y-2">
          <div
            onClick={() => {
              if (disabled) {
                toast.error(lockedMessage ?? "Subscribe to upload audio.");
                return;
              }
              audioInputRef.current?.click();
            }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition ${
              disabled
                ? "cursor-not-allowed border-border bg-muted/40 opacity-70"
                : "cursor-pointer border-border bg-background hover:border-primary/40"
            }`}
          >
            {disabled ? (
              <Lock className="h-5 w-5 text-muted-foreground" />
            ) : audioLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : audioFileName ? (
              <Mic className="h-5 w-5 text-primary" />
            ) : (
              <Upload className="h-5 w-5 text-muted-foreground" />
            )}
            <div className="text-sm">
              {disabled ? (
                <span className="font-medium text-muted-foreground">{lockedMessage ?? "Subscribe to upload audio"}</span>
              ) : audioLoading ? (
                <>Transcribing <span className="font-medium">{audioFileName}</span>… this can take a minute.</>
              ) : audioFileName ? (
                <><span className="font-medium">{audioFileName}</span> transcribed — drop another to replace</>
              ) : (
                <>
                  <span className="font-medium text-foreground">Drop a lecture recording</span>{" "}
                  <span className="text-muted-foreground">or click to upload</span>
                </>
              )}
            </div>
            <div className="text-xs text-muted-foreground">.mp3 · .m4a · .wav · .mp4 · .webm (max 20 MB, ~30 min)</div>
            <input
              ref={audioInputRef}
              type="file"
              disabled={disabled}
              accept="audio/*,video/mp4,video/webm,.mp3,.m4a,.wav,.mp4,.webm,.ogg"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleAudioFile(file);
                e.target.value = "";
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            We'll transcribe the recording with AI and use the text as your source. Lectures, class recordings, voice memos all work.
          </p>
          {value && !audioLoading && audioFileName && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Transcript ready</span> — {value.split(/\s+/).length.toLocaleString()} words. You can edit it below before generating.
            </div>
          )}
        </div>
      )}

      {/* Always show editable text below YouTube/audio so user can clean up */}
      {(mode === "youtube" || mode === "audio") && value && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={disabled}
          className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

function guessMime(name: string): string {
  const n = name.toLowerCase();
  if (n.endsWith(".mp3")) return "audio/mpeg";
  if (n.endsWith(".m4a")) return "audio/mp4";
  if (n.endsWith(".wav")) return "audio/wav";
  if (n.endsWith(".ogg")) return "audio/ogg";
  if (n.endsWith(".webm")) return "video/webm";
  if (n.endsWith(".mp4")) return "video/mp4";
  return "audio/mpeg";
}
